import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@ghezaa/shell";
import { inMarket, type BcStatus, type ReportSpec, type ReportView, type TargetMeasure } from "./client";

export function useSetUpMarket() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; currency: string; timeZone: string }) => api<{ code: string }>("/api/markets", { method: "POST", json: body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["markets"] }),
  });
}

export function useUpdateMarket(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { currency: string; timeZone: string }) => api<void>(inMarket(market), { method: "PUT", json: body }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["markets"] }),
  });
}

/** Linking reads the company at once; the answer is the page's new status. */
export function useLinkCompany(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => api<BcStatus>(`${inMarket(market)}/business-central/link`, { method: "PUT", json: { companyId } }),
    onSuccess: (status) => {
      client.setQueryData(["bc", market], status);
      void client.invalidateQueries({ queryKey: ["markets"] });
      void client.invalidateQueries({ queryKey: ["customers", market] });
      void client.invalidateQueries({ queryKey: ["people", market] });
    },
  });
}

export function useReadBc(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<BcStatus>(`${inMarket(market)}/business-central/read`, { method: "POST" }),
    onSuccess: (status) => {
      client.setQueryData(["bc", market], status);
      void client.invalidateQueries({ queryKey: ["customers", market] });
      void client.invalidateQueries({ queryKey: ["customer", market] });
      void client.invalidateQueries({ queryKey: ["people", market] });
    },
  });
}

/** The sales read now; `again` reads every entry from the start. */
export function useReadSales(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (again: boolean) => api<BcStatus>(`${inMarket(market)}/business-central/read-sales${again ? "?again=true" : ""}`, { method: "POST" }),
    onSuccess: (status) => {
      client.setQueryData(["bc", market], status);
      for (const key of ["today", "team", "customers", "customer", "data-gaps"]) void client.invalidateQueries({ queryKey: [key, market] });
    },
  });
}

export type TargetInput = { measure: TargetMeasure; value: number; weight: number };

function invalidateTargets(client: ReturnType<typeof useQueryClient>, market: string) {
  for (const key of ["targets", "today", "team"]) void client.invalidateQueries({ queryKey: [key, market] });
}

/** A code's targets for a month, replaced whole; an empty list clears them. */
export function useSetTargets(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ month, code, targets }: { month: string; code: string; targets: TargetInput[] }) =>
      api<void>(`${inMarket(market)}/targets/${month}/${encodeURIComponent(code)}`, { method: "PUT", json: { targets } }),
    onSuccess: () => invalidateTargets(client, market),
  });
}

/** The month before's targets, for every code with none set in this month. */
export function useCopyTargets(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (month: string) => api<{ copied: number }>(`${inMarket(market)}/targets/${month}/copy`, { method: "POST" }),
    onSuccess: () => invalidateTargets(client, market),
  });
}

export type LinkPerson = { userId: string | null; managerUserId: string | null; notAPerson: boolean };

export function useLinkPerson(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ code, ...body }: LinkPerson & { code: string }) => api<void>(`${inMarket(market)}/people/${encodeURIComponent(code)}`, { method: "PUT", json: body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["people", market] });
      void client.invalidateQueries({ queryKey: ["me", market] });
      void client.invalidateQueries({ queryKey: ["customers", market] });
    },
  });
}

/** A saved view's answer goes into its own query and the list is asked again. */
function useViewSaved(market: string) {
  const client = useQueryClient();
  return (view: ReportView) => {
    client.setQueryData(["report-views", market, view.id], view);
    void client.invalidateQueries({ queryKey: ["report-views", market], exact: true });
  };
}

export function useCreateView(market: string) {
  const saved = useViewSaved(market);
  return useMutation({
    mutationFn: (body: { name: string; spec: ReportSpec }) => api<ReportView>(`${inMarket(market)}/reports/views`, { method: "POST", json: body }),
    onSuccess: saved,
  });
}

export function useUpdateView(market: string, id: string) {
  const saved = useViewSaved(market);
  return useMutation({
    mutationFn: (body: { name: string; spec: ReportSpec }) => api<ReportView>(`${inMarket(market)}/reports/views/${encodeURIComponent(id)}`, { method: "PUT", json: body }),
    onSuccess: saved,
  });
}

export function useShareView(market: string, id: string) {
  const saved = useViewSaved(market);
  return useMutation({
    mutationFn: (shared: boolean) => api<ReportView>(`${inMarket(market)}/reports/views/${encodeURIComponent(id)}/shared`, { method: "PUT", json: { shared } }),
    onSuccess: saved,
  });
}

export function useDeleteView(market: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`${inMarket(market)}/reports/views/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: (_, id) => {
      client.removeQueries({ queryKey: ["report-views", market, id] });
      void client.invalidateQueries({ queryKey: ["report-views", market], exact: true });
    },
  });
}
